/**
 * @file AdminPanel.js
 * @description View para o painel de administração.
 */
import React, { useState, useRef } from "react";
import {
  PlusCircle,
  Trash2,
  Search,
  Loader,
  Users,
  RefreshCw,
  AlertTriangle,
  UserPlus,
  Star,
  Upload,
} from "lucide-react";
import * as api from "../api/ligaApi";
import { useModal } from "../contexts/ModalContext";

const AdminPanel = ({
  equipes,
  admins,
  onAddEquipe,
  onRemoveEquipe,
  onAddTime,
  onRemoveTime,
  onFetchScores,
  onUpdateAllTimes,
  onResetScores,
  onResetScoresForRound,
  onAddAdmin,
  onDeleteAdmin,
  onSetCaptain,
  onUploadLogo,
}) => {
  const modal = useModal();
  const [newEquipeName, setNewEquipeName] = useState("");
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [rodadaAdmin, setRodadaAdmin] = useState(1);
  const [isFetchingScores, setIsFetchingScores] = useState(false);
  const [isUpdatingTeams, setIsUpdatingTeams] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isResettingRound, setIsResettingRound] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const fileInputRefs = useRef({});

  const handleLogoButtonClick = (equipeId) => {
    fileInputRefs.current[equipeId].click();
  };

  const handleFileChange = (e, equipeId) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("logo", file);
      onUploadLogo(equipeId, formData);
    }
  };

  const handleAddEquipeSubmit = (e) => {
    e.preventDefault();
    onAddEquipe(newEquipeName);
    setNewEquipeName("");
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    if (!query) return;
    setIsSearching(true);
    setSearchError("");
    try {
      setSearchResults(await api.searchCartolaTimes(query));
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddTimeClick = async (equipeId, time) => {
    try {
      await onAddTime(equipeId, {
        nome_time: time.nome,
        nome_cartoleiro: time.nome_cartola,
        escudo_url: time.url_escudo_png,
        cartola_time_id: time.time_id,
        slug: time.slug,
      });
      setQuery("");
      setSearchResults([]);
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleFetchScoresClick = async () => {
    setIsFetchingScores(true);
    try {
      const result = await onFetchScores(rodadaAdmin);
      modal.showAlert({
        title: "Sucesso",
        message: result.message,
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsFetchingScores(false);
    }
  };

  const handleUpdateTeamsClick = async () => {
    setIsUpdatingTeams(true);
    try {
      const result = await onUpdateAllTimes();
      modal.showAlert({
        title: "Sucesso",
        message: result.message,
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsUpdatingTeams(false);
    }
  };

  const handleResetClick = async () => {
    setIsResetting(true);
    try {
      const result = await onResetScores();
      if (result) {
        modal.showAlert({
          title: "Sucesso",
          message: result.message,
          type: "success",
        });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetRoundClick = async () => {
    setIsResettingRound(true);
    try {
      const result = await onResetScoresForRound(rodadaAdmin);
      if (result) {
        modal.showAlert({
          title: "Sucesso",
          message: result.message,
          type: "success",
        });
      }
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    } finally {
      setIsResettingRound(false);
    }
  };

  const handleAddAdminSubmit = (e) => {
    e.preventDefault();
    onAddAdmin({ email: newAdminEmail, password: newAdminPassword });
    setNewAdminEmail("");
    setNewAdminPassword("");
  };

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-teal-400">
        Painel de Administração
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold mb-4">Gerir Equipes e Times</h3>
          <form
            onSubmit={handleAddEquipeSubmit}
            className="flex items-center space-x-2 mb-6"
          >
            <input
              type="text"
              value={newEquipeName}
              onChange={(e) => setNewEquipeName(e.target.value)}
              placeholder="Nome da Nova Equipe"
              className="flex-grow bg-gray-700 text-white p-2 rounded-md text-sm sm:text-base"
            />
            <button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white font-bold p-2 rounded-lg flex items-center"
            >
              <PlusCircle size={18} />
            </button>
          </form>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {equipes.map((equipe) => (
              <div key={equipe.id} className="bg-gray-900/70 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={api.getEscudoUrl(equipe.logo_url) || '/logo-equipe-padrao.png'}
                      alt="Logo"
                      className="w-10 h-10 rounded-full object-contain"
                    />
                    <h4 className="text-lg font-bold text-teal-300">
                      {equipe.nome}
                    </h4>
                    <input
                      type="file"
                      ref={(el) => (fileInputRefs.current[equipe.id] = el)}
                      className="hidden"
                      onChange={(e) => handleFileChange(e, equipe.id)}
                      accept="image/png, image/jpeg"
                    />
                    <button
                      onClick={() => handleLogoButtonClick(equipe.id)}
                      className="text-gray-400 hover:text-white"
                      title="Fazer upload do logo da equipe"
                    >
                      <Upload size={16} />
                    </button>
                  </div>
                  <button
                    onClick={() => onRemoveEquipe(equipe.id)}
                    className="text-red-500 hover:text-red-400 p-1"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
                <div className="space-y-2 mb-3">
                  {equipe.times
                    .sort((a, b) => a.nome_time.localeCompare(b.nome_time))
                    .map((time) => (
                      <div
                        key={time.id}
                        className="bg-gray-700/50 p-2 rounded-lg flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <button
                            onClick={() => onSetCaptain(time.id, time.equipe_id)}
                            className="mr-2 p-1 rounded-full hover:bg-gray-600 transition-colors"
                            title="Definir como capitão"
                          >
                            <Star
                              size={16}
                              className={
                                time.is_captain
                                  ? "text-yellow-400 fill-yellow-400"
                                  : "text-gray-500"
                              }
                            />
                          </button>
                          <img
                            src={api.getEscudoUrl(time.escudo_url)}
                            alt={`Escudo do ${time.nome_time}`}
                            className="w-8 h-8 rounded-full mr-2"
                          />
                          <div>
                            <p className="font-semibold text-sm">
                              {time.nome_time}
                            </p>
                            <p className="text-xs text-gray-400">
                              {time.nome_cartoleiro}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => onRemoveTime(time.id)}
                          className="text-red-500 hover:text-red-400 p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  {equipe.times.length === 0 && (
                    <p className="text-gray-400 italic text-sm">
                      Nenhum time adicionado.
                    </p>
                  )}
                </div>
                {equipe.times.length < 5 && (
                  <div className="bg-gray-800 p-3 rounded-md">
                    <h5 className="text-md font-semibold mb-2">
                      Adicionar Time do Cartola
                    </h5>
                    <form
                      onSubmit={handleSearchSubmit}
                      className="flex items-center space-x-2 mb-3"
                    >
                      <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar time..."
                        className="flex-grow bg-gray-700 p-2 rounded-md text-sm sm:text-base"
                      />
                      <button
                        type="submit"
                        className="bg-blue-600 hover:bg-blue-700 p-2 rounded-lg"
                        disabled={isSearching}
                      >
                        {isSearching ? (
                          <Loader className="animate-spin" />
                        ) : (
                          <Search />
                        )}
                      </button>
                    </form>
                    {searchError && (
                      <p className="text-red-400 text-sm mb-2">{searchError}</p>
                    )}
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                      {searchResults.map((time) => (
                        <div
                          key={time.time_id}
                          className="bg-gray-900/50 p-2 rounded-lg flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <img
                              src={api.getEscudoUrl(time.url_escudo_png)}
                              alt={`Escudo do ${time.nome}`}
                              className="w-8 h-8 rounded-full mr-2"
                            />
                            <div>
                              <p className="text-sm">{time.nome}</p>
                              <p className="text-xs text-gray-400">
                                {time.nome_cartola}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddTimeClick(equipe.id, time)}
                            className="bg-green-600 hover:bg-green-700 p-1 rounded-full text-white"
                          >
                            <PlusCircle size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-8">
          <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">Ações da Liga</h3>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Sincronizar Pontuações</h4>
                <div className="flex items-center space-x-4 mb-3">
                  <label htmlFor="rodada-select-admin">Rodada:</label>
                  <select
                    id="rodada-select-admin"
                    value={rodadaAdmin}
                    onChange={(e) => setRodadaAdmin(Number(e.target.value))}
                    className="bg-gray-700 text-white p-2 rounded-md"
                  >
                    {[...Array(38).keys()].map((i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={handleFetchScoresClick}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center disabled:bg-gray-500 text-sm"
                    disabled={isFetchingScores}
                  >
                    {isFetchingScores ? (
                      <Loader className="animate-spin mr-2" />
                    ) : (
                      <RefreshCw size={16} className="mr-2" />
                    )}
                    {isFetchingScores ? "Buscando..." : "Buscar Pontos"}
                  </button>
                  <button
                    onClick={handleResetRoundClick}
                    className="w-full bg-red-800 hover:bg-red-700 text-white font-bold py-2 px-3 rounded-lg flex items-center justify-center disabled:bg-gray-500 text-sm"
                    disabled={isResettingRound}
                  >
                    {isResettingRound ? (
                      <Loader className="animate-spin mr-2" />
                    ) : (
                      <Trash2 size={16} className="mr-2" />
                    )}
                    {isResettingRound ? "Apagando..." : "Resetar Rodada"}
                  </button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  Atualizar Dados dos Times
                </h4>
                <p className="text-gray-400 mb-3 text-sm">
                  Busca os nomes e escudos mais recentes de todos os times da
                  liga.
                </p>
                <button
                  onClick={handleUpdateTeamsClick}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:bg-gray-500"
                  disabled={isUpdatingTeams}
                >
                  {isUpdatingTeams ? (
                    <Loader className="animate-spin mr-2" />
                  ) : (
                    <Users size={18} className="mr-2" />
                  )}
                  {isUpdatingTeams ? "Atualizando..." : "Atualizar Times"}
                </button>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-red-400">
                  Resetar Todas as Pontuações
                </h4>
                <p className="text-gray-400 mb-3 text-sm">
                  Apaga permanentemente todas as pontuações. Esta ação não pode
                  ser desfeita.
                </p>
                <button
                  onClick={handleResetClick}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center disabled:bg-gray-500"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <Loader className="animate-spin mr-2" />
                  ) : (
                    <AlertTriangle size={18} className="mr-2" />
                  )}
                  {isResetting ? "Resetando..." : "Resetar Tudo"}
                </button>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold mb-4">Gerir Administradores</h3>
            <form onSubmit={handleAddAdminSubmit} className="space-y-4 mb-6">
              <h4 className="font-semibold">Adicionar Novo Admin</h4>
              <input
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="Email do novo admin"
                required
                className="w-full bg-gray-700 text-white p-2 rounded-md"
              />
              <input
                type="password"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="Senha (mín. 6 caracteres)"
                required
                minLength="6"
                className="w-full bg-gray-700 text-white p-2 rounded-md"
              />
              <button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center"
              >
                <UserPlus size={18} className="mr-2" />
                Adicionar Admin
              </button>
            </form>
            <div>
              <h4 className="font-semibold mb-2">Admins Atuais</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                {admins.map((admin) => (
                  <div
                    key={admin.id}
                    className="bg-gray-700/50 p-2 rounded-lg flex items-center justify-between"
                  >
                    <p className="text-sm">{admin.email}</p>
                    <button
                      onClick={() => onDeleteAdmin(admin.id)}
                      className="text-red-500 hover:text-red-400 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;